<template>
    <div
        class="answer-tags"
        :class="{
            compact: compact,
            'with-label': withLabel,
            'display-dots': display === 'dots',
            'is-empty': display === 'dots' && !tags.length,
            'is-open': popupOpen,
        }"
        @click.stop
    >
        <div v-if="withLabel" class="tags-label">
            <Icon icon-class="ui-tag-01" size="3" />
            <span>{{ labelText }}</span>
        </div>

        <div class="tags-content">
            <template v-if="display !== 'dots'">
                <span
                    v-for="tag in tags"
                    :key="tag.id"
                    class="tag-pill"
                >
                    <span
                        class="tag-ball"
                        :style="{ backgroundColor: tag.color || '#6C7A89' }"
                    ></span>
                    <span class="tag-pill-name">{{ tag.name }}</span>
                    <button
                        type="button"
                        class="tag-pill-remove"
                        :title="t('remove')"
                        :disabled="busy"
                        @click.stop="remove(tag)"
                    >
                        <Icon icon-class="ui-x-close" size="1" />
                    </button>
                </span>
            </template>

            <kv-popup
                ref="popup"
                placement="bottom left"
                max-width="300px"
                min-width="273px"
                @after-open="onPopupOpen"
                @after-close="onPopupClose"
            >
                <template #trigger="{ toggle, isOpen }">
                    <button
                        v-if="display === 'dots' && tags.length"
                        type="button"
                        class="tag-dots-trigger"
                        :class="{ open: isOpen }"
                        :disabled="busy"
                        :title="tagsTitle"
                        @click.stop="toggle"
                    >
                        <span
                            v-for="tag in visibleDotTags"
                            :key="tag.id"
                            class="tag-dot"
                            :style="{ backgroundColor: tag.color || '#6C7A89' }"
                        ></span>
                    </button>
                    <button
                        v-else
                        type="button"
                        class="tag-trigger"
                        :class="{
                            open: isOpen,
                            plus: trigger === 'plus',
                            'dashed-tag': display === 'dots',
                        }"
                        :disabled="busy"
                        :title="t('inbox-message-add_tag')"
                        @click.stop="toggle"
                    >
                        <Icon
                            v-if="trigger === 'plus' && display !== 'dots'"
                            icon-class="ui-plus"
                            size="3"
                        />
                        <Icon v-else icon-class="ui-tag-01" size="3" />
                    </button>
                </template>

                <template #content>
                    <div class="tag-popup" @click.stop>
                        <template v-if="!editingTag">
                            <input
                                ref="input"
                                type="text"
                                class="tag-input"
                                :placeholder="
                                    placeholder ||
                                    t('inbox-message-tag_placeholder')
                                "
                                :disabled="busy"
                                v-model="query"
                                @keydown.enter.prevent="commitFirst"
                                @keydown.esc.prevent="closePopup"
                                @keydown.down.prevent="highlightNext"
                                @keydown.up.prevent="highlightPrev"
                            />

                            <div
                                class="tag-suggestions"
                                v-if="suggestionRows.length"
                            >
                                <div
                                    v-for="(row, index) in suggestionRows"
                                    :key="row.key"
                                    class="tag-suggestion"
                                    :class="{
                                        active: index === highlighted,
                                        create: row.create,
                                        applied: row.applied,
                                    }"
                                    @mouseenter="highlighted = index"
                                >
                                    <button
                                        type="button"
                                        class="tag-suggestion-main"
                                        :disabled="busy"
                                        @mousedown.prevent="pick(row)"
                                    >
                                        <span
                                            class="tag-ball"
                                            :style="{
                                                backgroundColor:
                                                    row.color || '#6C7A89',
                                            }"
                                        ></span>
                                        <span class="label">{{ row.label }}</span>
                                        <span class="hint" v-if="row.create">
                                            {{ t("inbox-message-create_tag") }}
                                        </span>
                                        <Icon
                                            v-else-if="row.applied"
                                            class="tag-applied-check"
                                            icon-class="ui-check"
                                            size="2"
                                        />
                                    </button>
                                    <button
                                        v-if="!row.create"
                                        type="button"
                                        class="tag-suggestion-menu"
                                        :title="t('inbox-message-edit_tag')"
                                        :disabled="busy"
                                        @mousedown.prevent="
                                            openEditor(row)
                                        "
                                    >
                                        <span class="mdi mdi-dots-horizontal"></span>
                                    </button>
                                </div>
                            </div>

                            <div class="tag-empty" v-else-if="!query.trim()">
                                {{ t("inbox-message-tag_empty") }}
                            </div>
                        </template>

                        <div v-else class="tag-editor" @click.stop>
                            <button
                                type="button"
                                class="tag-editor-back"
                                @mousedown.prevent="closeEditor"
                            >
                                <Icon icon-class="ui-arrow-left" size="3" />
                            </button>
                            <input
                                ref="editName"
                                type="text"
                                class="tag-input"
                                v-model="editName"
                                :disabled="busy"
                                @keydown.enter.prevent="saveEditor"
                                @keydown.esc.prevent="closeEditor"
                            />

                            <div class="tag-editor-colors">
                                <div class="tag-editor-colors-label">
                                    {{ t("inbox-message-select_tag_color") }}
                                </div>
                                <div class="tag-color-grid">
                                    <button
                                        v-for="color in colorPalette"
                                        :key="color"
                                        type="button"
                                        class="tag-color-swatch"
                                        :class="{
                                            active:
                                                normalizeHex(editColor) ===
                                                color,
                                        }"
                                        :style="{ backgroundColor: color }"
                                        :aria-label="color"
                                        @mousedown.prevent="editColor = color"
                                    ></button>
                                </div>
                                <div class="tag-color-hex-row">
                                    <span
                                        class="tag-color-preview"
                                        :style="{
                                            backgroundColor:
                                                editColor || '#6C7A89',
                                        }"
                                    ></span>
                                    <input
                                        type="text"
                                        class="tag-hex-input"
                                        :value="hexDisplay"
                                        maxlength="7"
                                        :disabled="busy"
                                        @input="onHexInput"
                                    />
                                </div>
                            </div>

                            <button
                                type="button"
                                class="tag-editor-save"
                                :disabled="busy || !editName.trim()"
                                @mousedown.prevent="saveEditor"
                            >
                                {{ t("inbox-message-save") }}
                            </button>

                            <button
                                type="button"
                                class="tag-editor-delete"
                                :disabled="busy"
                                @mousedown.prevent="deleteEditingTag"
                            >
                                <Icon icon-class="ui-trash-01" size="3" />
                                <span>{{ t("inbox-message-delete_tag") }}</span>
                            </button>
                        </div>
                    </div>
                </template>
            </kv-popup>
        </div>
    </div>
</template>

<script>
import Icon from "../components/icon/icon";
import KvPopup from "../components/ui/kv-popup.vue";

const COLOR_PALETTE = [
    "#FA121D",
    "#FF5A36",
    "#FF8A00",
    "#F1C40F",
    "#FFE14D",
    "#B6E63A",
    "#2ECC71",
    "#1ABC9C",
    "#16A085",
    "#00C2A8",
    "#3498DB",
    "#2E86DE",
    "#5B6CFF",
    "#7C5CFF",
    "#9B59B6",
    "#C56CF0",
    "#E056FD",
    "#FD79A8",
    "#E84393",
    "#E74C3C",
    "#C0392B",
    "#A0522D",
    "#8D6E63",
    "#6C7A89",
    "#34495E",
    "#2C3E50",
    "#1F2937",
    "#111827",
    "#9DA4AE",
    "#D2D6DB",
    "#E5E7EB",
    "#F3F4F6",
];

export default {
    name: "answer-tags",
    components: { Icon, KvPopup },
    props: {
        tags: {
            type: Array,
            default: () => [],
        },
        // Catalog of tags for this scope (suggestions). Creating is always allowed.
        suggestions: {
            type: Array,
            default: () => [],
        },
        compact: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: "",
        },
        busy: {
            type: Boolean,
            default: false,
        },
        trigger: {
            type: String,
            default: "tag", // "tag" | "plus"
        },
        withLabel: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: "",
        },
        // "pills" shows named chips; "dots" shows overlapping color indicators.
        display: {
            type: String,
            default: "pills", // "pills" | "dots"
        },
        maxDots: {
            type: Number,
            default: 5,
        },
    },
    data() {
        return {
            query: "",
            highlighted: 0,
            editingTag: null,
            editName: "",
            editColor: "#6C7A89",
            colorPalette: COLOR_PALETTE,
            popupOpen: false,
        };
    },
    computed: {
        labelText() {
            return this.label || this.t("inbox-message-tags_label");
        },
        appliedIds() {
            return (this.tags || []).map((tag) => String(tag.id));
        },
        visibleDotTags() {
            return (this.tags || []).slice(0, this.maxDots);
        },
        tagsTitle() {
            return (this.tags || [])
                .map((tag) => tag.name)
                .filter(Boolean)
                .join(", ");
        },
        hexDisplay() {
            return String(this.editColor || "")
                .replace(/^#/, "")
                .toUpperCase();
        },
        suggestionRows() {
            const q = this.query.trim().toLowerCase();
            const catalog = this.suggestions || [];
            const filtered = q
                ? catalog.filter((tag) =>
                      String(tag.name).toLowerCase().includes(q),
                  )
                : catalog;

            const rows = filtered.map((tag) => ({
                key: "tag-" + tag.id,
                id: tag.id,
                name: tag.name,
                color: tag.color,
                label: tag.name,
                create: false,
                applied: this.appliedIds.indexOf(String(tag.id)) !== -1,
            }));

            const exact = catalog.some(
                (tag) => String(tag.name).toLowerCase() === q,
            );
            if (q && !exact) {
                rows.unshift({
                    key: "create-" + q,
                    name: this.query.trim(),
                    color: "#6C7A89",
                    label: this.query.trim(),
                    create: true,
                    applied: false,
                });
            }
            return rows;
        },
    },
    watch: {
        suggestionRows() {
            if (this.highlighted >= this.suggestionRows.length) {
                this.highlighted = 0;
            }
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        normalizeHex(value) {
            if (!value) return "";
            let hex = String(value).trim();
            if (hex.charAt(0) !== "#") hex = "#" + hex;
            if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return "";
            return hex.toUpperCase();
        },
        onPopupOpen() {
            this.popupOpen = true;
            this.focusInput();
        },
        onPopupClose() {
            this.popupOpen = false;
            this.resetPopup();
        },
        focusInput() {
            this.$nextTick(() => {
                const el = this.editingTag
                    ? this.$refs.editName
                    : this.$refs.input;
                if (el) el.focus();
            });
        },
        resetPopup() {
            this.query = "";
            this.highlighted = 0;
            this.editingTag = null;
            this.editName = "";
            this.editColor = "#6C7A89";
        },
        closePopup() {
            if (this.$refs.popup) {
                this.$refs.popup.toggleShow(false);
            }
        },
        highlightNext() {
            if (!this.suggestionRows.length) return;
            this.highlighted =
                (this.highlighted + 1) % this.suggestionRows.length;
        },
        highlightPrev() {
            if (!this.suggestionRows.length) return;
            this.highlighted =
                (this.highlighted - 1 + this.suggestionRows.length) %
                this.suggestionRows.length;
        },
        commitFirst() {
            if (!this.suggestionRows.length) {
                const name = this.query.trim();
                if (name) {
                    this.emitApply({ name });
                }
                return;
            }
            this.pick(this.suggestionRows[this.highlighted]);
        },
        pick(row) {
            if (!row) return;
            if (row.applied) {
                const tag = (this.tags || []).find(
                    (item) => String(item.id) === String(row.id),
                );
                if (tag) {
                    this.remove(tag);
                }
                return;
            }
            if (row.create || !row.id) {
                this.emitApply({ name: row.name });
            } else {
                this.emitApply({ tag_id: row.id });
            }
        },
        emitApply(payload) {
            this.query = "";
            this.highlighted = 0;
            this.closePopup();
            this.$emit("apply", payload);
        },
        remove(tag) {
            this.$emit("remove", tag);
        },
        openEditor(row) {
            this.editingTag = {
                id: row.id,
                name: row.name,
                color: row.color,
            };
            this.editName = row.name || "";
            this.editColor = this.normalizeHex(row.color) || "#6C7A89";
            this.$nextTick(() => this.focusInput());
        },
        closeEditor() {
            this.editingTag = null;
            this.editName = "";
            this.editColor = "#6C7A89";
            this.$nextTick(() => this.focusInput());
        },
        onHexInput(event) {
            const raw = String(event.target.value || "")
                .replace(/[^0-9a-fA-F]/g, "")
                .slice(0, 6);
            event.target.value = raw.toUpperCase();
            const normalized = this.normalizeHex(raw);
            if (normalized) {
                this.editColor = normalized;
            }
        },
        saveEditor() {
            if (!this.editingTag || !this.editName.trim()) return;
            const color =
                this.normalizeHex(this.editColor) ||
                this.editingTag.color ||
                "#6C7A89";
            this.$emit("update", {
                id: this.editingTag.id,
                name: this.editName.trim(),
                color,
            });
            this.closeEditor();
        },
        deleteEditingTag() {
            if (!this.editingTag) return;
            const tag = { ...this.editingTag };
            this.$emit("delete", tag);
            this.closeEditor();
        },
    },
};
</script>

<style lang="less" scoped>
.answer-tags {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    position: relative;
    max-width: 100%;

    &.with-label {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        gap: 16px;
    }

    .tags-label {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        font-weight: 400;
        color: #6c737f;
        flex-shrink: 0;
    }

    .tags-content {
        display: inline-flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        min-width: 0;
        justify-content: flex-end;
    }

    &.with-label .tags-content {
        flex: 1 1 auto;
    }

    &.display-dots {
        display: inline-flex;
        flex-wrap: nowrap;
        max-width: none;
    }
}

.tag-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 8px 0 10px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #384250;
    font-size: 13px;
    font-weight: 400;
    line-height: 1;
    max-width: 100%;

    .tag-pill-name {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .tag-pill-remove {
        border: none;
        background: transparent;
        color: #9da4ae;
        padding: 0;
        display: inline-flex;
        cursor: pointer;
        line-height: 1;
        flex-shrink: 0;

        &:hover {
            color: #384250;
        }

        &:disabled {
            opacity: 0.5;
            cursor: default;
        }
    }
}

.tag-ball {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.tag-trigger {
    box-sizing: border-box;
    width: 28px;
    height: 28px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    color: #6c737f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    &:hover,
    &.open {
        background: #f3f4f6;
        border-color: #d2d6db;
        color: #384250;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }

    &.plus,
    &.dashed-tag {
        border-radius: 999px;
        border-style: dashed;
        background: transparent;

        &:hover,
        &.open {
            background: #f9fafb;
            border-color: #9da4ae;
        }
    }
}

.tag-dots-trigger {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    height: 28px;
    padding: 0 8px;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    background: #fff;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s, border-color 0.15s;

    &:hover,
    &.open {
        background: #f9fafb;
        border-color: #d2d6db;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }

    .tag-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        border: 1.5px solid #fff;
        box-sizing: border-box;
        flex-shrink: 0;

        & + .tag-dot {
            margin-left: -4px;
        }
    }
}

.answer-tags.compact .tag-trigger:not(.plus):not(.dashed-tag) {
    width: 28px;
    height: 28px;
    border-radius: 999px;
}

.answer-tags.compact .tag-trigger.dashed-tag {
    width: 28px;
    height: 28px;
    border-radius: 999px;
}

.answer-tags.compact .tag-pill {
    height: 24px;
    font-size: 12px;
    padding: 0 6px 0 8px;
}

.tag-popup {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.tag-input {
    width: 100%;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 400;
    color: #384250;
    outline: none;
    box-sizing: border-box;

    &::placeholder {
        color: #9da4ae;
    }

    &:focus {
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.12);
    }

    &:disabled {
        opacity: 0.6;
    }
}

.tag-suggestions {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 220px;
    overflow-y: auto;
}

.tag-suggestion {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 2px;
    border: 1px solid #eef0f3;
    border-radius: 10px;
    background: #fff;
    padding: 2px;

    &.active,
    &:hover {
        border-color: #e5e7eb;
        background: #f9fafb;
    }
}

.tag-applied-check {
    color: var(--primary);
    flex-shrink: 0;
    margin-left: 4px;
}

.tag-suggestion-main {
    flex: 1 1 auto;
    min-width: 0;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;
    text-align: left;
    color: #384250;
    font-size: 13px;
    font-weight: 400;

    &:disabled {
        cursor: default;
    }

    .label {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: 400;
    }

    .hint {
        font-size: 11px;
        color: var(--primary);
        flex-shrink: 0;
        font-weight: 500;
    }
}

.tag-suggestion-menu {
    width: 28px;
    height: 28px;
    border: none;
    background: transparent;
    color: #9da4ae;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    padding: 0;

    &:hover {
        background: #f3f4f6;
        color: #384250;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }

    .mdi {
        font-size: 18px;
        line-height: 1;
    }
}

.tag-empty {
    font-size: 12px;
    color: #9da4ae;
    padding: 4px 6px;
    font-weight: 400;
}

.tag-editor {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.tag-editor-back {
    width: 28px;
    height: 28px;
    border: none;
    background: transparent;
    color: #6c737f;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;

    &:hover {
        background: #f3f4f6;
        color: #384250;
    }
}

.tag-editor-colors-label {
    font-size: 12px;
    color: #6c737f;
    margin-bottom: 8px;
}

.tag-color-grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 6px;
}

.tag-color-swatch {
    width: 100%;
    aspect-ratio: 1;
    border: 2px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    padding: 0;

    &.active {
        border-color: #111927;
        box-shadow: 0 0 0 1px #fff inset;
    }
}

.tag-color-hex-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
}

.tag-color-preview {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    flex-shrink: 0;
}

.tag-hex-input {
    flex: 1;
    height: 32px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0 10px;
    font-size: 13px;
    color: #384250;
    outline: none;
    text-transform: uppercase;

    &:focus {
        border-color: var(--primary);
    }
}

.tag-editor-save {
    width: 100%;
    height: 36px;
    border: none;
    border-radius: 10px;
    background: var(--primary);
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
}

.tag-editor-delete {
    width: 100%;
    height: 40px;
    border: none;
    border-radius: 10px;
    background: #fef3f2;
    color: #b42318;
    font-size: 13px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;

    &:hover {
        background: #fee4e2;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
}
</style>
